// E-ii.3 (2026-09-23): one-time Terms of Service acceptance for Collector users.
// Wraps protected routes. Asks the backend once per signed-in user whether the
// current Terms version has been accepted; if not, shows a blocking prompt.
// SystemAdmin and pre-migration databases report required=false (no prompt).
import React, { useEffect, useState } from 'react';
import apiClient from '../api';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';

// Remember the result per user for this page load so route changes don't refetch.
const checkedUsers = new Set();

const TermsAcceptanceGate = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const userId = user?.id;
  const [status, setStatus] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !userId || checkedUsers.has(userId)) return;
    let cancelled = false;
    apiClient.get('/auth/terms-status')
      .then((res) => {
        if (cancelled) return;
        if (res.data?.required) {
          setStatus(res.data);
        } else {
          checkedUsers.add(userId);
        }
      })
      .catch(() => { /* never lock users out because the check failed */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, userId]);

  const handleAccept = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.post('/auth/accept-terms', { version: status.current_version });
      checkedUsers.add(userId);
      setStatus(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError((detail && (detail.message || detail)) || 'Could not record your acceptance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const required = status?.required && !checkedUsers.has(userId);

  return (
    <>
      {children}
      {required && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-gate-title"
          data-testid="terms-gate"
        >
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 id="terms-gate-title" className="text-xl font-semibold text-slate-900">
              {status.accepted_version ? 'Our Terms of Service have been updated' : 'Terms of Service'}
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Before you continue, please review and accept the Terms of Service and Privacy Policy.
              Your office&apos;s subscription is also governed by its separate agreement with
              Real Estate Connect Technologies, LLC.
            </p>
            <p className="mt-3 text-sm">
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Read the Terms of Service</a>
              <span className="mx-2 text-slate-400">&middot;</span>
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Read the Privacy Policy</a>
            </p>
            <label className="mt-5 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 flex-shrink-0"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="terms-gate-checkbox"
              />
              <span>I have read and agree to the Terms of Service and Privacy Policy.</span>
            </label>
            {error && <p className="mt-3 text-sm text-red-600">{String(error)}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => logout && logout()} disabled={saving}>
                Sign out
              </Button>
              <Button onClick={handleAccept} disabled={!agreed || saving} data-testid="terms-gate-accept">
                {saving ? 'Saving...' : 'Accept and continue'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TermsAcceptanceGate;
